import { useEffect } from 'react';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { updateCurrentAccount } from 'app/store/Accounts/Accounts.async';
// Selectors
import { selectCurrentAccount, selectLoading } from 'app/store/Accounts/Accounts.selectors';
import { selectHasAnyActiveSubscriptionPackageRecurring } from 'app/store/BillingSubscriptions/BillingSubscriptions.selectors';
// Mui
import { Container, Paper, Box } from '@mui/material';
// Components
import { Button, Switch } from 'app/components/Mui';
import BillingEmails from 'app/components/BillingEmails';

interface IFormData {
  splitInvoicePerCase: boolean;
  emails: string[];
}

export default function BillingPreferencesTabContent() {
  const { t } = useTranslation();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const currentAccount = useAppSelector(selectCurrentAccount);
  const loading = useAppSelector(selectLoading);
  const hasAnyActiveSubscriptionPackageRecurring = useAppSelector(selectHasAnyActiveSubscriptionPackageRecurring);

  const methods = useForm<IFormData>({
    defaultValues: {
      splitInvoicePerCase: false,
      emails: []
    }
  });
  const { control, handleSubmit } = methods;

  const onSubmit = handleSubmit((data:IFormData) => {
    if ( !currentAccount ) return;
    const { status, quickBooksId, features, ...restCurrentAccount } = currentAccount;
    const billingPreferencesData:any = {}
    if ( data.emails?.length ) billingPreferencesData['emails'] = data.emails;
    if ( hasAnyActiveSubscriptionPackageRecurring ) billingPreferencesData['splitInvoicePerCase'] = data.splitInvoicePerCase;
    dispatch(updateCurrentAccount({
      ...restCurrentAccount,
      settings: {
        ...restCurrentAccount.settings,
        billingPreferences: billingPreferencesData
      }
    }));
  });

  useEffect(() => {
    if ( !currentAccount ) return;
    methods.reset({
      splitInvoicePerCase: typeof currentAccount.settings.billingPreferences?.splitInvoicePerCase !== 'undefined'
        ? currentAccount.settings.billingPreferences.splitInvoicePerCase
        : false,
      emails: currentAccount.settings.billingPreferences?.emails || []
    });
    // eslint-disable-next-line
  }, [currentAccount])

  return (
    <FormProvider {...methods}>
      <Container sx={{ pt: 4, pb: 4 }} maxWidth="md">
        <Paper sx={{ p: 4 }} elevation={2} component="form" onSubmit={onSubmit} noValidate>
          <BillingEmails
            label={t('pages.companyAccount.billingEmailsLabel')}
            helperText={t('pages.companyAccount.billingEmailHelperText')}
          />
          {hasAnyActiveSubscriptionPackageRecurring ? (
            <Controller
              control={control} name="splitInvoicePerCase"
              render={({ field:{ onChange, ...restFields }}) => (
                <Switch
                  {...restFields}
                  sx={{ pt: 0 }}
                  label={t('labels.splitInvoicePerCase')}
                  onChange={(_, checked:boolean) => onChange(checked)}
                  helperText={t('pages.billingAndSubscriptions.splitInvoicePerCaseHelperText')}
                />
              )}
            />
          ) : null}
          <Box sx={{ pt: 4, textAlign: 'right' }}>
            <Button
              name="Save company account form"
              loading={loading}
              type="submit"
              color="primary"
              variant="contained"
            >{t('buttons.save')}</Button>
          </Box>
        </Paper>
      </Container>
    </FormProvider>
  )
}
